/**
 * The requirements as a TABLE — one row per enquiry (Akhil, 2026-09-15).
 *
 * The board is the right shape for a dozen live requirements and the wrong one for hundreds:
 * five columns of cards mean scrolling each column separately to find anything, and nothing
 * lines up. A list puts the enquiry number, customer, vessel, status, age and quotation number
 * on one line each, sortable by eye, and the same filter bar narrows both views.
 *
 * Read-only by design: the board's Won / Rejected decision stays on the board card, where its
 * confirmation and the reply badges live. A row opens the requirement exactly as a card does.
 */

import { useMemo, useState } from 'react'
import { canSyncRequirement } from '../../lib/wonDecision'
import { Badge } from '../../components/Badge'
import { Button, Card } from '../../components/ui'
import { formatMoney } from '../../lib/format'
import {
  activeColumnFilterCount,
  applyColumnFilters,
  columnOptions,
  EMPTY_COLUMN_FILTERS,
  type ColumnFilters,
} from '../../lib/requirementColumnFilters'
import type { EnquirySummary } from '../../lib/types'

const cellInput: React.CSSProperties = { width: '100%', fontSize: 11.5, padding: '2px 6px', minWidth: 80 }

const STATUS_TONE: Record<string, 'muted' | 'info' | 'warn' | 'ok' | 'danger' | 'violet'> = {
  Open: 'muted',
  'Items Matched': 'info',
  'RFQ Sent': 'info',
  'Quotes Received': 'warn',
  'Comparison Done': 'violet',
  'Quotation Sent': 'ok',
  Won: 'ok',
  Rejected: 'danger',
  Archived: 'muted',
}

export function RequirementsList({
  requirements,
  onOpen,
  onDuplicate,
  duplicatingId,
  onSync,
  syncingId,
}: {
  requirements: EnquirySummary[]
  onOpen: (requirement: EnquirySummary) => void
  /** Copy a priced requirement for another order — see `RequirementCard`. */
  onDuplicate?: (requirement: EnquirySummary) => void
  duplicatingId?: string | null
  /** Re-send a won chain to the ERP. Shown only on a Won — see `lib/wonDecision.ts`. */
  onSync?: (requirement: EnquirySummary) => void
  syncingId?: string | null
}) {
  /*
    A filter under every heading (Akhil, 2026-09-15) — where the eye is, for a list of hundreds.
    Session state only, composed with AND on top of the filter bar's rows. The choice columns
    offer only what is on screen (`columnOptions`), so no option yields an empty table.
  */
  const [filters, setFilters] = useState<ColumnFilters>(EMPTY_COLUMN_FILTERS)
  const set = (patch: Partial<ColumnFilters>) => setFilters((f) => ({ ...f, ...patch }))
  const options = useMemo(() => columnOptions(requirements), [requirements])
  const rows = useMemo(() => applyColumnFilters(requirements, filters), [requirements, filters])
  const active = activeColumnFilterCount(filters)

  const choice = (key: 'status' | 'enquiry_type' | 'purchaser', values: string[]) => (
    <select value={filters[key]} onChange={(e) => set({ [key]: e.target.value } as Partial<ColumnFilters>)} style={cellInput} aria-label={`Filter ${key}`}>
      <option value="">all</option>
      {values.map((v) => (
        <option key={v} value={v}>
          {v}
        </option>
      ))}
    </select>
  )
  const text = (key: 'enquiry' | 'customer' | 'vessel' | 'quotation', placeholder: string) => (
    <input type="search" value={filters[key]} placeholder={placeholder} onChange={(e) => set({ [key]: e.target.value } as Partial<ColumnFilters>)} style={cellInput} aria-label={`Filter ${key}`} />
  )

  return (
    <Card padded={false}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 12px', fontSize: 12, color: 'var(--muted)' }}>
        <span>
          {rows.length} of {requirements.length} row{requirements.length === 1 ? '' : 's'}
          {active > 0 ? ` · ${active} column filter${active === 1 ? '' : 's'}` : ''}
        </span>
        {active > 0 ? (
          <Button size="sm" onClick={() => setFilters(EMPTY_COLUMN_FILTERS)}>
            Clear column filters
          </Button>
        ) : null}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Enquiry</th>
              <th>Customer</th>
              <th>Vessel</th>
              <th>Type</th>
              <th>Status</th>
              <th className="right">Age</th>
              <th>Quotation</th>
              <th className="right">Value</th>
              <th>Purchaser</th>
              <th />
            </tr>
            <tr>
              <th>{text('enquiry', 'ENQ-… / ref')}</th>
              <th>{text('customer', 'customer…')}</th>
              <th>{text('vessel', 'vessel…')}</th>
              <th>{choice('enquiry_type', options.enquiry_type)}</th>
              <th>{choice('status', options.status)}</th>
              <th />
              <th>{text('quotation', 'QTN-…')}</th>
              <th />
              <th>{choice('purchaser', options.purchaser)}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ color: 'var(--muted)', textAlign: 'center', padding: 22 }}>
                  No requirement matches these column filters.
                </td>
              </tr>
            ) : null}
            {rows.map((r) => (
              <tr
                key={r.id}
                onClick={() => onOpen(r)}
                style={{ cursor: 'pointer' }}
                title="Open where the work continues"
              >
                <td>
                  <div className="mono" style={{ fontWeight: 700 }}>
                    {r.id}
                  </div>
                  {r.ref !== r.id ? <div style={{ fontSize: 11, color: 'var(--muted)' }}>{r.ref}</div> : null}
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>{r.date}</div>
                </td>
                <td style={{ whiteSpace: 'normal' }}>{r.customer_name}</td>
                <td className="mono">{r.vessel ?? '—'}</td>
                <td>{r.enquiry_type}</td>
                <td>
                  <Badge tone={STATUS_TONE[r.status] ?? 'muted'}>{r.status}</Badge>
                </td>
                <td className="right mono" title="Days since the status last changed">
                  {r.age_days}d
                </td>
                <td className="mono">{r.quotation_id ?? '—'}</td>
                <td className="right mono">{r.value.amount > 0 ? formatMoney(r.value) : '—'}</td>
                <td style={{ fontSize: 12, color: 'var(--muted)' }}>{r.purchaser}</td>
                <td>
                  {onDuplicate && r.quotation_id ? (
                    <Button
                      size="sm"
                      disabled={duplicatingId === r.id}
                      title={`Copy every line and the priced quotation ${r.quotation_id} into a new requirement`}
                      onClick={(e) => {
                        e.stopPropagation()
                        onDuplicate(r)
                      }}
                    >
                      {duplicatingId === r.id ? 'copying…' : '⧉ Duplicate'}
                    </Button>
                  ) : null}
                  {/* A Won whose push failed has no other way back to the ERP. */}
                  {onSync && canSyncRequirement(r) ? (
                    <Button
                      size="sm"
                      disabled={syncingId === r.id}
                      title="Send the enquiry, supplier quotations, comparison and quotation to the ERP again"
                      onClick={(e) => {
                        e.stopPropagation()
                        onSync(r)
                      }}
                    >
                      {syncingId === r.id ? 'syncing…' : '⟳ Sync'}
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}
